import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Description as QuotationIcon,
  ArrowBack as BackIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import ReportDataGrid from "../components/ReportDataGrid";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/apiService";
import image from "../assets/riyal.png";
import { formatDate } from "../utils/dateUtils";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/AuthContext";

const QuotationReport = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isVatAdmin = currentUser?.role === "vatadmin";
  const { t, language } = useTranslation();
  const isRtl = language === "ar";
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: "",
    converted: "",
    tenantId: "all",
  });

  const fetchCompanies = async () => {
    try {
      const res = await api.get("/vat-admin/my-companies", {
        params: { limit: "all" },
      });
      const records = res.data?.data ?? res.data ?? [];
      setCompanies(Array.isArray(records) ? records : []);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  useEffect(() => {
    if (isVatAdmin) {
      fetchCompanies();
    }
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);

      const params = {};
      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString().split("T")[0];
      }
      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString().split("T")[0];
      }
      if (language === "ar" || language === "en") {
        params.language = language;
      }

      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.converted !== "" && filters.converted !== undefined) {
        params.converted = filters.converted;
      }

      if (isVatAdmin && filters.tenantId && filters.tenantId !== "all") {
        params.tenantIds = filters.tenantId;
      }

      const response = await api.get("/reports/quotation-report", { params });

      if (response.data && response.data.success) {
        const apiData = response.data;

        const quotationsWithId = (apiData.data || []).map((quotation, index) => ({
          ...quotation,
          id: quotation.id || quotation.quotationNo || quotation.quotationNumber || `quotation-${index}`,
        }));

        setReportData(quotationsWithId);
        setSummary(apiData.summary || null);
      }
    } catch (err) {
      console.error("Error fetching quotation report:", err);
      alert(t("Reports.Failed_To_Fetch_Quotation"));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const columns = [
    {
      field: "quotationNo",
      headerName: t("Reports.Quotation_Number"),
      flex: 1,
      minWidth: 130,
    },
    {
      field: "date",
      headerName: t("Reports.Date"),
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "customer",
      headerName: t("Reports.Customer"),
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: "validUntil",
      headerName: t("Reports.Valid_Until"),
      flex: 1,
      minWidth: 120,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "status",
      headerName: t("Reports.Status"),
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const val = params.value?.toLowerCase();
        const statusLabel =
          val === "accepted"
            ? t("Reports.Accepted")
            : val === "sent"
              ? t("Reports.Sent")
              : val === "rejected"
                ? t("Reports.Rejected")
                : val === "expired"
                  ? t("Reports.Expired")
                  : params.value;

        return (
          <Chip
            label={statusLabel}
            size="small"
            color={
              val === "accepted"
                ? "success"
                : val === "sent"
                  ? "info"
                  : val === "rejected"
                    ? "error"
                    : val === "expired"
                      ? "warning"
                      : "default"
            }
            sx={{ fontWeight: 600, textTransform: "capitalize" }}
          />
        );
      },
    },
    {
      field: "amount",
      headerName: t("Reports.Amount"),
      flex: 1,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            width: "100%",
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </Box>
      ),
    },
    {
      field: "vat",
      headerName: t("Reports.VAT"),
      flex: 0.8,
      minWidth: 120,
      type: "number",
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            width: "100%",
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </div>
      ),
    },
    {
      field: "total",
      headerName: t("Reports.Total"),
      flex: 1,
      minWidth: 130,
      type: "number",
      align: "right",
      headerAlign: "right",
      cellClassName: "font-weight-bold",
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            width: "100%",
            fontWeight: "bold",
          }}
        >
          <img
            src={image}
            alt="Riyal"
            style={{
              width: 18,
              height: 18,
              objectFit: "contain",
            }}
          />
          <span>
            {params.value?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) || "0.00"}
          </span>
        </div>
      ),
    },
    {
      field: "convertedToInvoice",
      headerName: t("Reports.Converted"),
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.row.invoiceNo || t("Reports.Yes")}
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        ) : (
          <Chip
            label={t("Reports.No")}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container
        maxWidth={false}
        sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
      >
        <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={() => navigate("/reports")}
                sx={{
                  mr: isRtl ? 0 : 1,
                  ml: isRtl ? 1 : 0,
                  bgcolor: "rgba(67, 97, 238, 0.1)",
                }}
              >
                <BackIcon
                  sx={{ transform: isRtl ? "rotate(180deg)" : "none" }}
                />
              </IconButton>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  {t("Reports.Quotation_Report")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {t("Reports.Quotation_Report_Subtitle")}
                </Typography>
              </Box>
            </Box>
            <Box>
              <IconButton
                sx={{
                  color: "#2196f3",
                  border: "1px solid #2196f3",
                  "&:hover": {
                    backgroundColor: "rgba(33, 150, 243, 0.08)",
                  },
                }}
              >
                <AssessmentIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
                <DatePicker
                  label={t("Reports.Start_Date")}
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange("startDate", date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
                <DatePicker
                  label={t("Reports.End_Date")}
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange("endDate", date)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                        },
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isVatAdmin ? 2 : 3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("Reports.Conversion_Status")}</InputLabel>
                  <Select
                    value={filters.converted}
                    onChange={(e) =>
                      handleFilterChange("converted", e.target.value)
                    }
                    label={t("Reports.Conversion_Status")}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: 0,
                      },
                    }}
                  >
                    <MenuItem value="">{t("Reports.All")}</MenuItem>
                    <MenuItem value="true">{t("Reports.Converted")}</MenuItem>
                    <MenuItem value="false">
                      {t("Reports.Not_Converted")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {isVatAdmin && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("Common.Company_Name") || "Company"}</InputLabel>
                    <Select
                      value={filters.tenantId}
                      onChange={(e) =>
                        handleFilterChange("tenantId", e.target.value)
                      }
                      label={t("Common.Company_Name") || "Company"}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: 0,
                        },
                      }}
                    >
                      <MenuItem value="all">
                        {t("Common.All_Companies") || "All Companies"}
                      </MenuItem>
                      {companies.map((company) => (
                        <MenuItem key={company.tenantId} value={company.tenantId}>
                          {company.name || company.tenantId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={isVatAdmin ? 3 : 3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilters}
                  disabled={loading}
                  sx={{ height: "40px", borderRadius: 0 }}
                >
                  {loading ? (
                    <CircularProgress size={20} />
                  ) : (
                    t("Reports.Apply_Filters")
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {summary && (
            <Grid container spacing={1} sx={{ mb: 0.5 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: isRtl ? "none" : "4px solid #4361ee",
                    borderRight: isRtl ? "4px solid #4361ee" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#4361ee15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                      }}
                    >
                      <QuotationIcon sx={{ color: "#4361ee", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("Reports.Total_Quotations")}
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {summary.totalQuotations || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: isRtl ? "none" : "4px solid #7c4dff",
                    borderRight: isRtl ? "4px solid #7c4dff" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#7c4dff15",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                      }}
                    >
                      <MoneyIcon sx={{ color: "#7c4dff", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("Reports.Total_Amount")}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <img
                          src={image}
                          alt=""
                          style={{
                            width: "20px",
                            height: "20px",
                            objectFit: "contain",
                          }}
                        />
                        <span>
                          {summary.totalValue?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || "0.00"}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: "4px solid #ff9800",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#ff980015",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                      }}
                    >
                      <TrendingUpIcon sx={{ color: "#ff9800", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("Reports.Total_VAT")}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="700"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <img
                          src={image}
                          alt=""
                          style={{
                            width: "20px",
                            height: "20px",
                            objectFit: "contain",
                          }}
                        />
                        <span>
                          {summary.totalVAT?.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }) || "0.00"}
                        </span>
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: isRtl ? "none" : "4px solid #4caf50",
                    borderRight: isRtl ? "4px solid #4caf50" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#4caf5015",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                      }}
                    >
                      <ReceiptIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("Reports.Converted")}
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {summary.converted || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 0,
                    borderLeft: isRtl ? "none" : "4px solid #f44336",
                    borderRight: isRtl ? "4px solid #f44336" : "none",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        bgcolor: "#f4433615",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: isRtl ? 0 : 1.5,
                        ml: isRtl ? 1.5 : 0,
                      }}
                    >
                      <QuotationIcon sx={{ color: "#f44336", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t("Reports.Not_Converted")}
                      </Typography>
                      <Typography variant="h6" fontWeight="700">
                        {summary.totalQuotations - summary.converted || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Data Grid */}
          <ReportDataGrid
            title={t("Reports.Quotation_Report")}
            data={reportData}
            columns={columns}
            loading={loading}
            pageSize={25}
            onRefresh={fetchReport}
            exportFileName="quotation-report"
            getRowId={(row) =>
              row.id || row.quotationNo || row.quotationNumber
            }
            totals={summary}
            dateRange={{
              startDate: filters.startDate,
              endDate: filters.endDate,
            }}
          />
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default QuotationReport;
